export const PRIMITIVES = {
  basic: {
    white: '#FFFFFF',
    black: '#000000',
  },
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E6E9EF',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  trueGrey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    300: '#D4D4D4',
    400: '#A3A3A3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },
  red: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },
  orange: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
  },
  green: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#10B981',
    600: '#059669',
    700: '#047857',
    800: '#065F46',
    900: '#064E3B',
  },
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
  },
  lightBlue: {
    50: '#F0F9FF',
    100: '#E0F2FE',
    200: '#BAE6FD',
    300: '#7DD3FC',
    400: '#38BDF8',
    500: '#0EA5E9',
    600: '#0284C7',
    700: '#0369A1',
    800: '#075985',
    900: '#0C4A6E',
  },
  warmGrey: {
    50: '#FAFAF9',
    100: '#F5F5F4',
    200: '#E7E5E4',
    300: '#D6D3D1',
    400: '#A8A29E',
    500: '#78716C',
    600: '#57534E',
    700: '#44403C',
    800: '#292524',
    900: '#1C1917',
  },
}

export const SEMANTIC = {
  light: PRIMITIVES.grey,
  dark: PRIMITIVES.trueGrey,
  informative: PRIMITIVES.blue,
  positive: PRIMITIVES.green,
  warning: PRIMITIVES.orange,
  negative: PRIMITIVES.red,
  brandLight: PRIMITIVES.blue,
  brandDark: PRIMITIVES.lightBlue,
}

export const TOKENS_LIGHT = {
  background: {
    default: PRIMITIVES.basic.white,
    'default-strong': PRIMITIVES.basic.white,
    neutral: {
      strong: SEMANTIC.light[600],
      muted: SEMANTIC.light[300],
      onSubtle: SEMANTIC.light[200],
      subtle: SEMANTIC.light[100],
      surface: SEMANTIC.light[50],
    },
  },
  content: {
    primary: SEMANTIC.light[900],
    secondary: SEMANTIC.light[600],
    tertiary: SEMANTIC.brandLight[600],
    'inverted-primary': SEMANTIC.light[50],
    'inverted-secondary': SEMANTIC.light[200],
    dark: SEMANTIC.light[900],
    positive: SEMANTIC.positive[500],
    warning: SEMANTIC.warning[500],
    negative: SEMANTIC.negative[500],
  },
  border: {
    interaction: {
      normal: SEMANTIC.brandLight[500],
      hover: SEMANTIC.brandLight[600],
      active: SEMANTIC.brandLight[700],
      selected: SEMANTIC.brandLight[600],
      neutral: {
        normal: SEMANTIC.light[300],
        hover: SEMANTIC.light[400],
        active: SEMANTIC.light[500],
        selected: SEMANTIC.light[400],
      },
      alert: PRIMITIVES.red[600],
    },
    neutral: {
      subtle: SEMANTIC.light[300],
      strong: SEMANTIC.light[400],
      onSubtle: {
        subtle: SEMANTIC.light[300],
        strong: SEMANTIC.light[400],
      },
    },
  },
  action: {
    primary: {
      default: SEMANTIC.brandLight[500],
      hover: SEMANTIC.brandLight[600],
      active: SEMANTIC.brandLight[700],
      selected: SEMANTIC.brandLight[600],
      subtle: {
        default: SEMANTIC.brandLight[50],
        hover: SEMANTIC.brandLight[100],
        active: SEMANTIC.brandLight[200],
        selected: SEMANTIC.brandLight[100],
      },
    },
    neutral: {
      default: SEMANTIC.light[800],
      hover: SEMANTIC.light[500],
      active: SEMANTIC.light[800],
      selected: SEMANTIC.light[900],
      subtle: {
        default: SEMANTIC.light[50],
        hover: SEMANTIC.light[100],
        active: SEMANTIC.light[200],
        selected: SEMANTIC.light[100],
      },
      onSubtle: {
        default: PRIMITIVES.basic.white,
        hover: SEMANTIC.light[100],
        active: SEMANTIC.light[200],
        selected: SEMANTIC.light[100],
      },
      onSubtleSecondary: {
        default: SEMANTIC.light[100],
        hover: SEMANTIC.light[200],
        active: SEMANTIC.light[300],
        selected: SEMANTIC.light[200],
      },
      onDefault: {
        default: PRIMITIVES.basic.white,
        hover: SEMANTIC.light[100],
        active: SEMANTIC.light[200],
        selected: SEMANTIC.light[100],
        inverted: {
          default: SEMANTIC.light[300],
          hover: SEMANTIC.light[400],
          active: SEMANTIC.light[500],
          selected: SEMANTIC.light[600],
        },
      },
    },
    onBlack: {
      default: SEMANTIC.dark[400],
      hover: SEMANTIC.dark[300],
      active: SEMANTIC.dark[500],
      selected: PRIMITIVES.basic.white,
    },
    'white-transparent': {
      subtle: {
        default: '#ffffff00',
        hover: '#ffffff33',
        active: '#ffffff4d',
        selected: '#ffffff33',
      },
      default: '#ffffffe6',
      hover: '#ffffffcc',
      active: '#ffffff99',
      selected: '#ffffffcc',
    },
    'black-transparent': {
      subtle: {
        default: '#00000000',
        hover: '#0000001a',
        active: '#00000033',
        selected: '#0000001a',
      },
      default: '#000000e6',
      hover: '#000000cc',
      active: '#00000099',
      selected: '#000000cc',
    },
  },
}

export const BORDER_LIGHT = {
  'article-card-border': '1px',
}

export const BORDER_DARK = {
  'article-card-border': '0px',
}

export const TOKENS_DARK = {
  background: {
    default: SEMANTIC.dark[700],
    'default-strong': SEMANTIC.dark[800],
    neutral: {
      strong: SEMANTIC.dark[500],
      muted: SEMANTIC.dark[600],
      onSubtle: SEMANTIC.dark[700],
      subtle: SEMANTIC.dark[800],
      surface: SEMANTIC.dark[900],
    },
  },
  content: {
    primary: SEMANTIC.dark[100],
    secondary: SEMANTIC.dark[300],
    'inverted-primary': SEMANTIC.dark[900],
    'inverted-secondary': SEMANTIC.dark[700],
    dark: SEMANTIC.dark[900],
    positive: SEMANTIC.positive[300],
    warning: SEMANTIC.warning[300],
    negative: SEMANTIC.negative[300],
  },
  border: {
    interaction: {
      normal: SEMANTIC.brandDark[400],
      hover: SEMANTIC.brandDark[500],
      active: SEMANTIC.brandDark[600],
      selected: SEMANTIC.brandDark[500],
      neutral: {
        normal: SEMANTIC.dark[600],
        hover: SEMANTIC.dark[500],
        active: SEMANTIC.dark[400],
        selected: SEMANTIC.dark[500],
      },
      alert: PRIMITIVES.red[300],
    },
    neutral: {
      subtle: SEMANTIC.dark[600],
      strong: SEMANTIC.dark[500],
      onSubtle: {
        subtle: SEMANTIC.dark[600],
        strong: SEMANTIC.dark[500],
      },
    },
  },
  action: {
    primary: {
      default: SEMANTIC.brandDark[400],
      hover: SEMANTIC.brandDark[500],
      active: SEMANTIC.brandDark[600],
      selected: SEMANTIC.brandDark[500],
      subtle: {
        default: SEMANTIC.brandDark[50],
        hover: SEMANTIC.brandDark[100],
        active: SEMANTIC.brandDark[200],
        selected: SEMANTIC.brandDark[100],
      },
    },
    neutral: {
      default: SEMANTIC.dark[200],
      hover: SEMANTIC.dark[400],
      active: SEMANTIC.dark[200],
      selected: SEMANTIC.dark[50],
      subtle: {
        default: SEMANTIC.dark[800],
        hover: SEMANTIC.dark[700],
        active: SEMANTIC.dark[600],
        selected: SEMANTIC.dark[700],
      },
      onSubtle: {
        default: SEMANTIC.dark[700],
        hover: SEMANTIC.dark[600],
        active: SEMANTIC.dark[500],
        selected: SEMANTIC.dark[600],
      },
      onSubtleSecondary: {
        default: SEMANTIC.dark[800],
        hover: SEMANTIC.dark[700],
        active: SEMANTIC.dark[600],
        selected: SEMANTIC.dark[700],
      },
      onDefault: {
        default: SEMANTIC.dark[600],
        hover: SEMANTIC.dark[500],
        active: SEMANTIC.dark[400],
        selected: SEMANTIC.dark[500],
        inverted: {
          default: SEMANTIC.dark[500],
          hover: SEMANTIC.dark[400],
          active: SEMANTIC.dark[300],
          selected: SEMANTIC.dark[200],
        },
      },
    },
    onBlack: {
      default: SEMANTIC.dark[400],
      hover: SEMANTIC.dark[300],
      active: SEMANTIC.dark[500],
      selected: PRIMITIVES.basic.white,
    },
    'white-transparent': {
      subtle: {
        default: '#ffffff00',
        hover: '#ffffff33',
        active: '#ffffff4d',
        selected: '#ffffff33',
      },
      default: '#ffffffe6',
      hover: '#ffffffcc',
      active: '#ffffff99',
      selected: '#ffffffcc',
    },
    'black-transparent': {
      subtle: {
        default: '#00000000',
        hover: '#0000001a',
        active: '#00000033',
        selected: '#0000001a',
      },
      default: '#000000e6',
      hover: '#000000cc',
      active: '#00000099',
      selected: '#000000cc',
    },
  },
}

export const RADIUS = {
  '2px': '2px',
  '4px': '4px',
  '6px': '6px',
  '8px': '8px',
  '12px': '12px',
  '24px': '24px',
}

export const SPACING = {
  '2px': '2px',
  '4px': '4px',
  '6px': '6px',
  '8px': '8px',
  '12px': '12px',
  '16px': '16px',
  '24px': '24px',
  '32px': '32px',
  '40px': '40px',
  '48px': '48px',
  '56px': '56px',
  '64px': '64px',
}

export const BOX_SHADOW = {
  'article-card': '0px 1px 2px 0px rgba(27, 36, 44, 0.12)',
  'searchbox-default': '0px 1px 2px 0px rgba(27, 36, 44, 0.12)',
  'searchbox-hover':
    '0px 2px 2px -1px rgba(27, 36, 44, 0.04), 0px 2px 8px -1px rgba(27, 36, 44, 0.08)',
  'searchbox-focus':
    '0px 2px 2px -1px rgba(27, 35, 44, 0.04), 0px 8px 16px -2px rgba(27, 36, 44, 0.12)',
  'trending-news-image':
    '0px 100px 80px 0px rgba(0, 0, 0, 0.07), 0px 41.778px 33.422px 0px rgba(0, 0, 0, 0.05), 0px 22.336px 17.869px 0px rgba(0, 0, 0, 0.04), 0px 12.522px 10.017px 0px rgba(0, 0, 0, 0.04), 0px 6.65px 5.32px 0px rgba(0, 0, 0, 0.03), 0px 2.767px 2.214px 0px rgba(0, 0, 0, 0.02)',
}

export const LINE_HEIGHT = {
  '13': '3.25rem',
}

export const BREAKPOINTS = {
  SM: 480,
  MD: 600,
  LG: 767,
  XL: 1024,
  XXL: 1280,
}

export const BREAKPOINTS_PX = {
  sm: BREAKPOINTS.SM + 'px', // Old phones or compact screens
  md: BREAKPOINTS.MD + 'px', // Larger phones (e.g. Pixel)
  lg: BREAKPOINTS.LG + 'px', // Mobile devices
  xl: BREAKPOINTS.XL + 'px', // Tablets + phones in landscape
  '2xl': BREAKPOINTS.XXL + 'px', // Desktops
}
